<script lang="ts">
    import { onDestroy, tick } from 'svelte';

    export let name: string;
    export let errors: Record<string, string[]>;
    export let label: string = '';
    export let length: number = 6;
    export let disabled: boolean = false;
    export let required: boolean = false;
    export let autofocus: boolean = false;
    export let onEnter: (() => void) | undefined = undefined;

    let digits: string[] = Array(length).fill('');
    let inputs: HTMLInputElement[] = [];

    errors[name] = [];

    export let value = '';
    $: value = digits.join('');

    // Expose a reset function for parent to call
    export function reset() {
        digits = Array(length).fill('');
        tick().then(() => inputs[0]?.focus());
    }

    export async function focusFirst() {
        await tick();
        inputs[0]?.focus();
    }

    const validate = (currentDigits: string[] = digits) => {
        const val = currentDigits.join('');
        errors[name] = [];
        if (required && val.length < length) errors[name].push(`Enter all ${length} digits`);
        if (val && !/^\d+$/.test(val)) errors[name].push('Digits only');
    };

    const handleInput = (e: Event, i: number) => {
        const raw = (e.currentTarget as HTMLInputElement).value.replace(/\D/g, '');
        // Take the last character typed (handles replacing existing digit)
        const ch = raw.slice(-1);
        digits[i] = ch;
        digits = [...digits];
        if (ch && i < length - 1) inputs[i + 1]?.focus();
        validate(digits);
    };

    const handleKeydown = (e: KeyboardEvent, i: number) => {
        if (e.key === 'Enter') {
            onEnter?.();
            return;
        }
        if (e.key === 'Backspace') {
            if (digits[i]) {
                // Clear current field
                digits[i] = '';
                digits = [...digits];
            } else if (i > 0) {
                // Move back and clear
                digits[i - 1] = '';
                digits = [...digits];
                inputs[i - 1]?.focus();
            }
            e.preventDefault();
        } else if (e.key === 'ArrowLeft' && i > 0) {
            inputs[i - 1]?.focus();
            e.preventDefault();
        } else if (e.key === 'ArrowRight' && i < length - 1) {
            inputs[i + 1]?.focus();
            e.preventDefault();
        }
    };

    const handleFocus = (e: FocusEvent) => {
        (e.currentTarget as HTMLInputElement).select();
    };

    onDestroy(() => { errors[name] = []; });
</script>

<div class="form-field">
    {#if label}<span class="label-text">{label}</span>{/if}
    <div class="pin-row" role="group" aria-label={label || 'PIN entry'}>
        {#each digits as digit, i}
            <!-- svelte-ignore a11y-autofocus -->
            <input
                type="password"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="1"
                bind:this={inputs[i]}
                value={digits[i]}
                {disabled}
                autofocus={autofocus && i === 0}
                class="pin-cell input-mono"
                class:error={errors[name].length > 0}
                on:input={(e) => handleInput(e, i)}
                on:keydown={(e) => handleKeydown(e, i)}
                on:focus={handleFocus}
            />
        {/each}
    </div>
    <span class="field-errors">
        {#each errors[name] as err}<span>{err}</span>{/each}
    </span>
</div>

<style lang="scss">
    .pin-row {
        display: flex;
        gap: 0.4em;
    }

    .pin-cell {
        width: 2.8em;
        height: 2.8em;
        text-align: center;
        padding: 0;
        margin: 0;
        font-size: 1.2em;
        letter-spacing: 0.05em;
    }
</style>
